import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, OnDestroy, output } from '@angular/core';
import { IProduktAuswahlDetails, TariffOptionSap, TariffSelectionCardData } from '@mwe/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TariffSelectionCardUiComponent } from '../tariff-selection-card/tariff-selection-card-ui.component';
import { TariffSelectionCardLogic } from '../services/tariff-selection-card.logic';
import { ServiceStateEnum } from '@mwe/constants';
import { LoggingService } from '@mwe/services';
import { getFirstAllesSicherOption, isArrayWithMinOneItem } from '@mwe/utils';
import { TariffSelectionStore } from '../services/tariff-selection.store';
import { HorizontalSwiperComponent } from '../../swiper';
import { Subscription } from 'rxjs';
import { ButtonComponent } from '../../buttons/button/button.component';
import { TranslateDirective } from '@ngx-translate/core';
import { ButtonLinkComponent } from '../../buttons/button-link/button-link.component';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { ExpanderComponent } from '../../expander/expander.component';

@Component({
  selector: 'mwe-tariffs-selection-button',
  templateUrl: './tariffs-selection-button.component.html',
  imports: [
    ButtonComponent,
    TranslateDirective,
    HorizontalSwiperComponent,
    TariffSelectionCardUiComponent,
    ButtonLinkComponent,
    InfoboxComponent,
    ExpanderComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA], //needed for swiper webcomponents
})
export class TariffsSelectionButtonComponent implements OnDestroy {
  private readonly modalService = inject(NgbModal);
  private readonly cardLogic = inject(TariffSelectionCardLogic);
  private readonly loggingService = inject(LoggingService);
  private readonly subscriptions = new Subscription();
  private readonly store = inject(TariffSelectionStore);

  readonly tariffSelected = output<TariffSelectionCardData>();

  readonly previousTariff = input<TariffSelectionCardData>();
  // changes how API calls are done, should only be used in conjunction with NewVoucherFormComponent
  readonly hasNewVoucher = input<boolean>(false);

  readonly items = computed<TariffSelectionCardData[]>(() => this.computeListItems(this.store.allProducts(), this.store.selectedTariffs()));
  readonly additionalOptionGroupName = computed<string>(() => this.store.additionalOptionGroupName());

  additionalSwiperOptions = {
    breakpoints: {
      1200: {
        slidesPerView: 3,
        spaceBetween: 40,
      },
      992: {
        slidesPerView: 2.4,
        spaceBetween: 20,
      },
      850: {
        slidesPerView: 2,
        spaceBetween: 20,
      },
      750: {
        slidesPerView: 1.8,
        spaceBetween: 20,
      },
      500: {
        slidesPerView: 1.4,
        spaceBetween: 20,
      },
      400: {
        slidesPerView: 1.1,
        spaceBetween: 20,
      },
      0: {
        slidesPerView: 1,
        spaceBetween: 20,
      },
    },
  };

  ngOnDestroy(): void {
    this.closeModal();
    this.subscriptions.unsubscribe();
  }

  openTariffsSelectionModal(content) {
    this.modalService.open(content, {
      fullscreen: true,
      windowClass: 'bg-gray--200',
      ariaLabelledBy: 'tariff-selections-open-modal-button',
    });
  }

  closeModal(): void {
    this.modalService.dismissAll();
    // restore VoucherFormComponent, todo refactor it so we can reuse logic/service for voucher-validation instead of VoucherFormComponent
    if (!this.hasNewVoucher()) this.cardLogic.checkInitialVoucherCode();
  }

  selectTariff(cardRef: TariffSelectionCardUiComponent): void {
    this.modalService.dismissAll();
    this.cardLogic.updateCardData(this.getSelectedTariffWithMergedUserAdditionalOptions(cardRef.selectedTariff(), this.previousTariff()));
    // restore VoucherFormComponent, todo refactor it so we can reuse logic/service for voucher-validation instead of VoucherFormComponent
    if (!this.hasNewVoucher()) this.cardLogic.checkInitialVoucherCode();
  }

  async onTariffUpdate(
    newSapOption: TariffOptionSap,
    tariffData: TariffSelectionCardData,
    ref: TariffSelectionCardUiComponent,
  ): Promise<void> {
    // ui update - here we have no store-connection, so we can directly manipulate :D
    ref.selectedTariff.update(old => ({
      ...old,
      state: ServiceStateEnum.LOADING,
    }));

    const everySelectionItemNotUpdated = tariffData.userSelection.filter(u => u.art !== newSapOption.art);
    const userSelection = [...everySelectionItemNotUpdated, newSapOption];

    try {
      const priceIndication = await this.cardLogic.getNewPriceIndicationForUpdatedTariffOption(tariffData, userSelection, true);
      ref.tariff = {
        ...tariffData,
        userSelection,
        priceIndication,
        state: ServiceStateEnum.SUCCESS,
      };
    } catch (error) {
      this.loggingService.logError(error, 'TariffSelectionButtonComponent/onTariffUpdate');
      ref.tariff = {
        ...tariffData,
        userSelection,
        state: ServiceStateEnum.ERROR,
      };
    }
  }

  onReloadAfterError(item: TariffSelectionCardData, ref: TariffSelectionCardUiComponent) {
    // retry with old state
    this.onTariffUpdate(item.userSelection[0], item, ref);
  }

  private getSelectedTariffWithMergedUserAdditionalOptions(
    selectedTariff: TariffSelectionCardData,
    previousTariff: TariffSelectionCardData,
  ) {
    const allesSicherOption = getFirstAllesSicherOption(previousTariff.userSelection);
    if (allesSicherOption) {
      const everySelectionItemNotUpdated = selectedTariff.userSelection.filter(u => u.art !== allesSicherOption.art);
      selectedTariff.userSelection = [...everySelectionItemNotUpdated, allesSicherOption];
    }
    return selectedTariff;
  }

  // params comes from signal() do not manipulate it!!!!
  private computeListItems(allProducts: IProduktAuswahlDetails[], selectionList: TariffSelectionCardData[]) {
    //this.previousItem().anlageId only for tariff-change
    const selectedTariff = selectionList.find(l => {
      return this.previousTariff().anlageId
        ? l.anlageId === this.previousTariff().anlageId
        : l.category.toLowerCase() === this.previousTariff().category.toLowerCase();
    });

    const otherProducts = allProducts
      .filter(p => {
        return (
          p.sparte.toLowerCase() === selectedTariff.category.toLowerCase() &&
          p.tarif.ISUTarifKey !== selectedTariff.tariffKey &&
          p.anlageId === this.previousTariff().anlageId
        ); // there is no anlageId in new-client so this is always true
      })
      .map(product => {
        const priceIndicationCopy = this.store
          .allPriceIndications()
          .find(priceIndication => product.tarif.ISUTarifKey === priceIndication.tarif.ISUTarifKey);

        if (!priceIndicationCopy) {
          return undefined;
        }

        const tariffOptionsSap = isArrayWithMinOneItem(product.tarif.tarifOptionen) ? [...product.tarif.tarifOptionen] : [];
        const userSelection = isArrayWithMinOneItem(product.tarif.tarifOptionen)
          ? [...product.tarif.tarifOptionen.filter(o => !!o.default)]
          : [];
        return {
          category: product.sparte,
          tariffKey: product.tarif.ISUTarifKey,
          tariffOptionsSap,
          userSelection,
          priceIndication: { ...priceIndicationCopy },
          anlageId: product.anlageId,
          state: ServiceStateEnum.SUCCESS,
        } as TariffSelectionCardData;
      })
      .filter(i => !!i);

    return [{ ...selectedTariff }, ...otherProducts];
  }
}
